/**
 * Presentation data for the EMD (Wholesaler) dashboard.
 *
 * Everything here is aggregated at call time from the real Jupiterol 852/867
 * extracts in `emd-source-data.ts`, so the filter badges and the click-to-
 * cross-filter behaviour recompute genuine numbers rather than swapping between
 * hand-written constants.
 *
 * Wiring the real API only touches this file: replace the fact-table imports
 * with an apiService.post() result following the filterService.filterQuery
 * subscription pattern used elsewhere in the app, and the derive* functions
 * keep working unchanged.
 */

import {
  CLASSES_OF_TRADE,
  CUSTOMERS,
  DCS,
  INVENTORY_FACTS,
  PRODUCT,
  REGIONS,
  SALES_FACTS,
  WEEKS,
  WHOLESALERS,
} from './emd-source-data';

// ─── Palette ──────────────────────────────────────────────────────────────────

/**
 * One colour per wholesaler, used by the map, legends, donut and trend lines so
 * a wholesaler keeps its identity across every tab. Sourced from the dimension
 * table rather than repeated here — the two used to drift, and because
 * cross-filtering matches visuals by name, a drifted name silently failed to
 * highlight with the rest of the dashboard.
 */
export const WHOLESALER_COLORS: Record<string, string> = WHOLESALERS.reduce(
  (map: Record<string, string>, w) => { map[w.name] = w.color; return map; },
  {}
);

export const EMD_COLORS = {
  primary:   '#1363DF',
  primaryLt: '#5BA4F5',
  returns:   '#F45C8C',
  doh:       '#8B5CF6',
  onHand:    '#22D3EE',
  sold:      '#F43F5E',
  ink:       '#101D42',
  muted:     '#737D88',
  border:    '#E8EAEE',
  grid:      '#EDF0F5',
};

export function wholesalerColor(name: string): string {
  return WHOLESALER_COLORS[name] ?? EMD_COLORS.primary;
}

/** Legend order, top to bottom. */
export const WHOLESALER_NAMES = WHOLESALERS.map((w) => w.name);

// ─── Filter model ─────────────────────────────────────────────────────────────

/** Dimensions a badge can filter on. `bucket` is a display setting, not a cut. */
export type EmdFilterKey =
  | 'wholesaler' | 'customer' | 'cot' | 'region' | 'location'
  | 'brand' | 'ndc' | 'bucket';

export interface EmdChip {
  label: string;
  /** Dimension this chip cuts on; ties the badge to the derivation below. */
  key: EmdFilterKey;
  /** Every value the chip's dropdown offers. */
  options: string[];
  /** Currently applied selection; drives the count badge. */
  selected: string[];
  /** Multi-select chips show a count badge and the include/exclude header. */
  multi: boolean;
}

/**
 * Applied cuts, as value lists per dimension. A dimension that is absent, or
 * that has every option selected, is treated as unfiltered — selecting all
 * seven wholesalers means the same thing as selecting none.
 */
export interface EmdFilter {
  wholesaler?: string[];
  customer?: string[];
  cot?: string[];
  region?: string[];
  location?: string[];
  /** Trailing weeks to report on; defaults to the full 52-week extract. */
  weeks?: number;
  /**
   * Confirmed trend-bar selection: week-ending ISO dates to include. Unlike
   * the badge dimensions this cut comes from clicking chart bars, and it is
   * held by EmdFilterService next to the chips (cleared by Reset).
   */
  periods?: string[];
  /**
   * Time Range badge: inclusive ISO date bounds on the week-ending dates.
   * Composes with `weeks` as an intersection — the trailing window is clamped
   * to the range.
   */
  fromDate?: string;
  toDate?: string;
}

/** Bounds of the extract, for the Time Range badge's date inputs. */
export const EMD_DATE_MIN = WEEKS[0];
export const EMD_DATE_MAX = WEEKS[WEEKS.length - 1];

const chip = (
  label: string,
  key: EmdFilterKey,
  options: string[],
  multi = true
): EmdChip => ({ label, key, options: [...options], selected: [...options], multi });

/**
 * Chips are rebuilt per tab rather than shared, so applying a cut on one tab
 * does not silently mutate another tab's badge state.
 *
 * There is no Time bucket badge: it was cosmetic (chipsToFilter ignores it) and
 * the per-tab W/M/Q/Y pills own bucketing where it matters.
 */
export function buildChips(tab: 'Summary' | 'Details' | 'Inventory'): EmdChip[] {
  const wholesaler = chip('Wholesaler', 'wholesaler', WHOLESALER_NAMES);
  const customer   = chip('Customer', 'customer', CUSTOMERS.map((c) => c.name));
  const cot        = chip('Class of Trade', 'cot', CLASSES_OF_TRADE);
  const region     = chip('Regions', 'region', REGIONS);
  const location   = chip('Location', 'location', DCS.map((d) => d.name));
  // Brand/NDC are single-select badges (one product in this extract), so each
  // shows its selected value with no count/include-exclude header.
  const brand      = chip('Brand', 'brand', [PRODUCT.brand], false);
  const ndc        = chip('NDC Material', 'ndc', [PRODUCT.ndc], false);

  if (tab === 'Details') {
    return [wholesaler, customer, cot, location, brand, ndc];
  }
  if (tab === 'Inventory') {
    return [wholesaler, location, region, brand, ndc];
  }
  return [wholesaler, customer, cot, region, location, brand, ndc];
}

/** Collapses applied badges into an EmdFilter, dropping select-all dimensions. */
export function chipsToFilter(chips: EmdChip[]): EmdFilter {
  const filter: EmdFilter = {};
  for (const c of chips) {
    if (c.key === 'bucket' || c.key === 'brand' || c.key === 'ndc') { continue; }
    // A full selection is the same as no cut, and skipping it keeps the hot
    // path from building set lookups that always answer true.
    if (c.selected.length === c.options.length) { continue; }
    (filter as any)[c.key] = [...c.selected];
  }
  return filter;
}

// ─── Filter resolution ────────────────────────────────────────────────────────
// Cuts are resolved once into index sets over the dimension tables, then the
// fact tuples are scanned against those. Customer-level dimensions (class of
// trade, region) collapse into the customer set, so the scan stays a couple of
// integer lookups per row.

type IndexSet = Set<number> | null; // null means "no cut on this dimension"

const asSet = (values: string[] | undefined): Set<string> | null =>
  values && values.length ? new Set(values) : null;

function wholesalerSet(f: EmdFilter): IndexSet {
  const names = asSet(f.wholesaler);
  if (!names) { return null; }
  const out = new Set<number>();
  WHOLESALERS.forEach((w, i) => { if (names.has(w.name)) { out.add(i); } });
  return out;
}

function customerSet(f: EmdFilter): IndexSet {
  const names = asSet(f.customer);
  const cots = asSet(f.cot);
  const regions = asSet(f.region);
  if (!names && !cots && !regions) { return null; }

  const out = new Set<number>();
  CUSTOMERS.forEach((c, i) => {
    if (names && !names.has(c.name)) { return; }
    if (cots && !cots.has(c.cot)) { return; }
    if (regions && !regions.has(c.region)) { return; }
    out.add(i);
  });
  return out;
}

function dcSet(f: EmdFilter): IndexSet {
  const names = asSet(f.location);
  const regions = asSet(f.region);
  const ws = wholesalerSet(f);
  if (!names && !regions && !ws) { return null; }

  const out = new Set<number>();
  DCS.forEach((d, i) => {
    if (names && !names.has(d.name)) { return; }
    if (regions && !regions.has(d.region)) { return; }
    if (ws && !ws.has(d.ws)) { return; }
    out.add(i);
  });
  return out;
}

/**
 * The two feeds do not end on the same week — 867 sales stop at 2026-03-12
 * while 852 inventory runs to 2026-03-26 — and the week axis is their union.
 * Reporting to the end of that axis gave every sales visual two trailing weeks
 * of zeroes, which reads as a collapse rather than as absent data, so each
 * derivation ends at the last week its own source actually populates.
 */
const lastWeek = (facts: { 0: number }[]): number =>
  facts.reduce((max, f) => (f[0] > max ? f[0] : max), 0);

const SALES_END = lastWeek(SALES_FACTS as any);
const INVENTORY_END = lastWeek(INVENTORY_FACTS as any);

/** Inclusive week range for the reporting window; the Time Range badge clamps
 *  it (ISO strings compare correctly). An empty intersection yields from > to,
 *  which every derive reads as "no data". */
function window(f: EmdFilter, end: number): { from: number; to: number } {
  const span = f.weeks ?? WEEKS.length;
  let from = Math.max(0, end + 1 - span);
  let to = end;
  if (f.fromDate) { while (from <= to && WEEKS[from] < f.fromDate) { from++; } }
  if (f.toDate) { while (to >= from && WEEKS[to] > f.toDate) { to--; } }
  return { from, to };
}

const has = (set: IndexSet, i: number) => !set || set.has(i);

/** Week indices allowed by a confirmed period selection; null = no cut. */
function periodIndexSet(f: EmdFilter): IndexSet {
  if (!f.periods || !f.periods.length) { return null; }
  const wanted = new Set(f.periods);
  const out = new Set<number>();
  WEEKS.forEach((iso, i) => { if (wanted.has(iso)) { out.add(i); } });
  return out;
}

// ─── Aggregation helpers ──────────────────────────────────────────────────────

const sum = (values: number[]): number => values.reduce((a, b) => a + b, 0);

/** Signed percentage change, formatted the way the KPI cards expect. */
function pctChange(current: number, prior: number): { text: string; up: boolean } {
  if (!prior) { return { text: current ? '+100%' : '0.0%', up: current >= 0 }; }
  const pct = ((current - prior) / prior) * 100;
  const rounded = Math.round(pct * 10) / 10;
  return { text: `${rounded >= 0 ? '+' : ''}${rounded.toFixed(1)}%`, up: rounded >= 0 };
}

export const money = (usd: number): string =>
  usd >= 1e9 ? `$${(usd / 1e9).toFixed(1)}B`
  : usd >= 1e6 ? `$${(usd / 1e6).toFixed(1)}M`
  : usd >= 1e3 ? `$${Math.round(usd / 1e3).toLocaleString('en-US')}K`
  : `$${Math.round(usd).toLocaleString('en-US')}`;

const num = (v: number): string => Math.round(v).toLocaleString('en-US');

/** "2026-03-27" -> "27 March, 2026", the grid's date-column header form. */
function longDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  const month = d.toLocaleString('en-US', { month: 'long' });
  return `${String(d.getDate()).padStart(2, '0')} ${month}, ${d.getFullYear()}`;
}

/** "2026-03-27" -> "03/27/26", the compact axis-label form. */
function shortDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y.slice(2)}`;
}


// ─── KPI cards ────────────────────────────────────────────────────────────────

export interface EmdKpi {
  title: string;
  value: string;
  change: string;
  changeLabel: string;
  /** Arrow shown next to the change. */
  direction: 'UP' | 'DOWN' | 'FLAT';
  /**
   * Whether the movement is good, which drives the colour. Kept separate from
   * `direction` because falling returns is a decrease *and* an improvement:
   * that card shows a green down arrow. Mirrors kpi-chart's reverseTrendColors.
   */
  positive: boolean;
  sparkline: number[];
  color: string;
}

/**
 * `goodWhenUp: false` inverts the colour, so a falling returns or DOH figure
 * reads as an improvement.
 */
function kpi(
  title: string,
  value: string,
  series: number[],
  color: string,
  goodWhenUp = true
): EmdKpi {
  // Compared against the preceding window of equal length, so the delta is
  // like-for-like whatever range the user picked.
  const half = Math.max(1, Math.floor(series.length / 2));
  const recent = sum(series.slice(-half));
  const prior = sum(series.slice(-half * 2, -half));
  const { text, up } = pctChange(recent, prior);

  return {
    title,
    value,
    change: text,
    changeLabel: 'vs prior period',
    direction: recent === prior ? 'FLAT' : up ? 'UP' : 'DOWN',
    positive: recent === prior ? true : up === goodWhenUp,
    sparkline: series.length ? series : [0],
    color,
  };
}

// ─── Summary tab ──────────────────────────────────────────────────────────────

export interface EmdSummaryData {
  kpis: EmdKpi[];
  wholesalerLocations: { name: string; locations: number }[];
  netSales: { name: string; units: number; usd: number; netPct: number; change: string; positive: boolean }[];
  donut: { name: string; value: number; usd: number; pct: number; pctUsd: number }[];
  mapBubble: { wholesaler: string; location: string; sales: number }[];
  trendBar: { period: string; sales_qty: number }[];
  trendLine: { period: string; metric: string; sales_usd: number }[];
  valueGrid: { label: string; r: number }[];
}

export function deriveSummary(filter: EmdFilter = {}): EmdSummaryData {
  const ws = wholesalerSet(filter);
  const cu = customerSet(filter);
  const pset = periodIndexSet(filter);
  const { from, to } = window(filter, SALES_END);

  const weekQty = new Array(WEEKS.length).fill(0);
  const weekUsd = new Array(WEEKS.length).fill(0);

  const byWs = WHOLESALERS.map(() => ({ qty: 0, usd: 0, ret: 0, prior: 0, recent: 0 }));
  const wsCustomers = WHOLESALERS.map(() => new Set<number>());
  // Bubbles are one per wholesaler and shipping city, so repeat accounts in a
  // city roll into a single plotted point.
  const byCity = new Map<string, { wholesaler: string; location: string; sales: number }>();

  const mid = from + Math.floor((to + 1 - from) / 2);

  for (const [w, wsi, cui, qty, usd] of SALES_FACTS) {
    if (w < from || w > to || !has(pset, w) || !has(ws, wsi) || !has(cu, cui)) { continue; }

    weekQty[w] += qty;
    weekUsd[w] += usd;

    const agg = byWs[wsi];
    agg.qty += qty;
    agg.usd += usd;
    if (w >= mid) { agg.recent += qty; } else { agg.prior += qty; }

    wsCustomers[wsi].add(cui);

    const customer = CUSTOMERS[cui];
    const name = WHOLESALERS[wsi].name;
    const location = `${customer.city}, ${customer.state}`;
    const key = `${wsi}|${location}`;
    const bubble = byCity.get(key);
    if (bubble) { bubble.sales += qty; }
    else { byCity.set(key, { wholesaler: name, location, sales: qty }); }
  }

  // Returns are an 852 measure: stock that came back to the distribution
  // centre or became unsaleable. There is no customer on that feed, so this
  // pass honours only the wholesaler and location cuts — a class-of-trade or
  // customer badge narrows sales without narrowing returns.
  const weekRet = new Array(WEEKS.length).fill(0);
  const returnDcs = dcSet({ wholesaler: filter.wholesaler, location: filter.location });
  for (const [w, dci, , , , ret] of INVENTORY_FACTS) {
    if (w < from || w > to || !has(pset, w) || !has(returnDcs, dci)) { continue; }
    weekRet[w] += ret;
    byWs[DCS[dci].ws].ret += ret;
  }

  // A confirmed period selection also drops the excluded weeks from the axis,
  // rather than drawing them as zeroes (which reads as a collapse).
  const idx: number[] = [];
  for (let w = from; w <= to; w++) { if (has(pset, w)) { idx.push(w); } }
  const qtySeries = idx.map((w) => weekQty[w]);
  const usdSeries = idx.map((w) => weekUsd[w]);
  const retSeries = idx.map((w) => weekRet[w]);
  const periods = idx.map((w) => WEEKS[w]);

  const totalQty = sum(qtySeries);
  const totalUsd = sum(usdSeries);

  // Wholesalers with no rows under the current cut are dropped rather than
  // listed at zero, so the legends shrink to what the map actually plots.
  const active = WHOLESALERS
    .map((w, i) => ({ name: w.name, i, ...byWs[i] }))
    .filter((w) => w.qty > 0 || w.ret > 0)
    .sort((a, b) => b.qty - a.qty);

  const bubbles = [...byCity.values()];
  const maxBubble = Math.max(1, ...bubbles.map((b) => b.sales));

  return {
    kpis: [
      kpi('Total sales', num(totalQty), qtySeries, EMD_COLORS.primary),
      kpi('Total sales, $', money(sum(usdSeries)), usdSeries, EMD_COLORS.primary),
      // Returns falling is an improvement, hence the inverted colour.
      kpi('Total returns', num(sum(retSeries)), retSeries, EMD_COLORS.returns, false),
    ],

    wholesalerLocations: active.map((w) => ({
      name: w.name,
      locations: wsCustomers[w.i].size,
    })),

    netSales: active.map((w) => {
      const gross = w.qty + w.ret;
      const { text, up } = pctChange(w.recent, w.prior);
      return {
        name: w.name,
        units: w.qty,
        usd: w.usd,
        netPct: gross ? Math.round((w.qty / gross) * 100) : 100,
        change: text,
        positive: up,
      };
    }),

    donut: active.map((w) => ({
      name: w.name,
      value: w.qty,
      usd: w.usd,
      pct: totalQty ? Math.round((w.qty / totalQty) * 100) : 0,
      pctUsd: totalUsd ? Math.round((w.usd / totalUsd) * 100) : 0,
    })),

    mapBubble: bubbles,

    trendBar: periods.map((period, i) => ({ period, sales_qty: qtySeries[i] })),

    trendLine: periods.map((period, i) => ({
      period,
      metric: 'SALES ($)',
      sales_usd: usdSeries[i],
    })),

    // Five evenly spaced buckets across the plotted range, so the size key
    // always describes the bubbles actually on screen.
    valueGrid: [0.2, 0.4, 0.6, 0.8, 1].map((step) => ({
      label: `<${Math.ceil((maxBubble * step) / 10) * 10}`,
      r: Math.round(8 + step * 16),
    })),
  };
}

export type EmdTrendBucket = 'W' | 'M' | 'Q' | 'Y';

/**
 * First calendar day of the bucket containing a week-ending date — the trend
 * charts' period keys. Exported so a tab can map between a clicked bar (one
 * bucket) and the member weeks it stages as a period selection.
 */
export function bucketKey(iso: string, bucket: EmdTrendBucket): string {
  if (bucket === 'W') { return iso; }
  const [y, m] = iso.split('-');
  if (bucket === 'M') { return `${y}-${m}-01`; }
  if (bucket === 'Q') {
    const q = Math.floor((Number(m) - 1) / 3) * 3 + 1;
    return `${y}-${String(q).padStart(2, '0')}-01`;
  }
  return `${y}-01-01`;
}

/**
 * Roll the Summary tab's weekly trend up to the W/M/Q/Y pill's bucket. Buckets
 * key on their first calendar day (month, quarter or year start) so the chart's
 * date axis keeps parsing them like any other period. Weekly input is returned
 * as-is; insertion order follows the chronological input, so no re-sort.
 */
export function bucketSummaryTrend(
  bar: { period: string; sales_qty: number }[],
  line: { period: string; metric: string; sales_usd: number }[],
  bucket: EmdTrendBucket
): {
  bar: { period: string; sales_qty: number }[];
  line: { period: string; metric: string; sales_usd: number }[];
} {
  if (bucket === 'W') { return { bar, line }; }

  const qtyByBucket = new Map<string, number>();
  for (const b of bar) {
    const k = bucketKey(b.period, bucket);
    qtyByBucket.set(k, (qtyByBucket.get(k) ?? 0) + b.sales_qty);
  }

  const metric = line[0]?.metric ?? 'SALES ($)';
  const usdByBucket = new Map<string, number>();
  for (const l of line) {
    const k = bucketKey(l.period, bucket);
    usdByBucket.set(k, (usdByBucket.get(k) ?? 0) + l.sales_usd);
  }

  return {
    bar: [...qtyByBucket].map(([period, sales_qty]) => ({ period, sales_qty })),
    line: [...usdByBucket].map(([period, sales_usd]) => ({ period, metric, sales_usd })),
  };
}

// ─── Details tab ──────────────────────────────────────────────────────────────

export interface DetailsTrendDatum {
  period: string;
  wholesaler: string;
  sales_qty: number;
  sales_usd: number;
}

export interface EmdDetailsData {
  series: string[];
  trend: DetailsTrendDatum[];
  dateColumns: string[];
  rows: any[];
}

export function deriveDetails(filter: EmdFilter = {}, bucket: EmdTrendBucket = 'M'): EmdDetailsData {
  const ws = wholesalerSet(filter);
  const cu = customerSet(filter);
  const pset = periodIndexSet(filter);
  const { from, to } = window(filter, SALES_END);

  // Trend buckets keyed by the W/M/Q/Y selector; the chart's x axis is fed the
  // same bucket so labels match. Each cell carries both units and dollars so
  // the header's Units/$ toggle can switch without re-deriving.
  const byPeriod = new Map<string, Map<number, { qty: number; usd: number }>>();
  // Wholesaler -> customer -> per-week quantities, for the expandable grid.
  const byWs = new Map<number, Map<number, number[]>>();
  const seriesTotals = WHOLESALERS.map(() => 0);

  for (const [w, wsi, cui, qty, usd] of SALES_FACTS) {
    if (w < from || w > to || !has(pset, w) || !has(ws, wsi) || !has(cu, cui)) { continue; }

    const period = bucketKey(WEEKS[w], bucket);
    let periods = byPeriod.get(period);
    if (!periods) { periods = new Map(); byPeriod.set(period, periods); }
    const cell = periods.get(wsi) ?? { qty: 0, usd: 0 };
    cell.qty += qty;
    cell.usd += usd;
    periods.set(wsi, cell);

    seriesTotals[wsi] += qty;

    let customers = byWs.get(wsi);
    if (!customers) { customers = new Map(); byWs.set(wsi, customers); }
    let weeks = customers.get(cui);
    if (!weeks) { weeks = new Array(WEEKS.length).fill(0); customers.set(cui, weeks); }
    weeks[w] += qty;
  }

  const series = WHOLESALERS
    .map((w, i) => ({ name: w.name, i, total: seriesTotals[i] }))
    .filter((w) => w.total > 0)
    .sort((a, b) => b.total - a.total);

  const periods = [...byPeriod.keys()].sort();
  const trend: DetailsTrendDatum[] = [];
  for (const s of series) {
    for (const period of periods) {
      const cell = byPeriod.get(period)?.get(s.i);
      trend.push({
        period,
        wholesaler: s.name,
        sales_qty: cell?.qty ?? 0,
        sales_usd: cell?.usd ?? 0,
      });
    }
  }

  // The grid shows the five most recent weeks in the window (of the selected
  // periods, when a trend-bar selection is applied).
  const cols = WEEKS.map((_, i) => i)
    .filter((i) => i >= from && i <= to && has(pset, i))
    .slice(-5);

  const rows = series.map((s) => {
    const customers = byWs.get(s.i) ?? new Map<number, number[]>();
    const children = [...customers.entries()]
      .map(([cui, weeks]) => ({
        wholesaler: CUSTOMERS[cui].name,
        total: sum(weeks.slice(from, to + 1)),
        ...cols.reduce((o: any, w, i) => { o[`d${i}`] = weeks[w]; return o; }, {}),
      }))
      .filter((c) => c.total > 0)
      .sort((a, b) => b.total - a.total);

    return {
      wholesaler: s.name,
      total: s.total,
      ...cols.reduce((o: any, _w, i) => {
        o[`d${i}`] = sum(children.map((c: any) => c[`d${i}`]));
        return o;
      }, {}),
      // `callRecords` is the child-row key that app-hierarchical-grid-chart's
      // detailCellRendererParams hardcodes.
      callRecords: children,
    };
  });

  return {
    series: series.map((s) => s.name),
    trend,
    dateColumns: cols.map((i) => longDate(WEEKS[i])),
    rows,
  };
}

// ─── Inventory tab ────────────────────────────────────────────────────────────

export const INVENTORY_METRICS = ['Avg. DOH', 'QTY On Hand', 'QTY Sold', 'QTY Received'];

export interface EmdInventoryData {
  kpis: EmdKpi[];
  labels: string[];
  balanceBar: { period: string; doh: number }[];
  balanceLine: { period: string; metric: string; qty: number }[];
  dohBars: { name: string; value: number }[];
  dateColumns: string[];
  rows: any[];
}

/**
 * Days on hand: ending inventory divided by the average daily quantity sold.
 * Weeks with no movement have no meaningful DOH, so they report 0 rather than
 * dividing by zero and pushing an Infinity into the chart's y scale.
 */
const daysOnHand = (ending: number, sold: number): number =>
  sold > 0 ? Math.round((ending / (sold / 7)) * 10) / 10 : 0;

export function deriveInventory(filter: EmdFilter = {}): EmdInventoryData {
  const dcs = dcSet(filter);
  const pset = periodIndexSet(filter);
  const { from, to } = window(filter, INVENTORY_END);

  const weekEnd = new Array(WEEKS.length).fill(0);
  const weekSold = new Array(WEEKS.length).fill(0);
  const weekRcvd = new Array(WEEKS.length).fill(0);

  // Wholesaler -> DC -> per-week ending inventory, for the expandable grid.
  const byWs = new Map<number, Map<number, number[]>>();
  const wsTotals = WHOLESALERS.map(() => ({ end: 0, sold: 0 }));

  for (const [w, dci, end, sold, rcvd] of INVENTORY_FACTS) {
    if (w < from || w > to || !has(pset, w) || !has(dcs, dci)) { continue; }

    weekEnd[w] += end;
    weekSold[w] += sold;
    weekRcvd[w] += rcvd;

    const wsi = DCS[dci].ws;
    wsTotals[wsi].end += end;
    wsTotals[wsi].sold += sold;

    let locations = byWs.get(wsi);
    if (!locations) { locations = new Map(); byWs.set(wsi, locations); }
    let weeks = locations.get(dci);
    if (!weeks) { weeks = new Array(WEEKS.length).fill(0); locations.set(dci, weeks); }
    weeks[w] += end;
  }

  // As in deriveSummary: excluded weeks leave the axis instead of plotting 0.
  const idx: number[] = [];
  for (let w = from; w <= to; w++) { if (has(pset, w)) { idx.push(w); } }
  const periods = idx.map((w) => WEEKS[w]);
  const endSeries = idx.map((w) => weekEnd[w]);
  const soldSeries = idx.map((w) => weekSold[w]);
  const rcvdSeries = idx.map((w) => weekRcvd[w]);
  const dohSeries = endSeries.map((end, i) => daysOnHand(end, soldSeries[i]));

  const totalSold = sum(soldSeries);
  const totalRcvd = sum(rcvdSeries);
  const movingWeeks = dohSeries.filter((d) => d > 0);
  const avgDoh = movingWeeks.length ? sum(movingWeeks) / movingWeeks.length : 0;
  // Sell-through against what was received, which is the quantity that could
  // have been sold in the window.
  const sellThrough = totalRcvd ? (totalSold / totalRcvd) * 100 : 0;

  const cols = WEEKS.map((_, i) => i)
    .filter((i) => i >= from && i <= to && has(pset, i))
    .slice(-4);

  const rows = WHOLESALERS
    .map((w, i) => ({ name: w.name, i }))
    .filter((w) => byWs.has(w.i))
    .map((w) => {
      const locations = byWs.get(w.i)!;
      const children = [...locations.entries()]
        .map(([dci, weeks]) => {
          // Stock is a level, not a flow, so the row reports the average
          // weekly position rather than a sum across weeks.
          const inWindow = weeks.slice(from, to + 1);
          const reported = inWindow.filter((v) => v > 0).length;
          return {
            wholesaler: DCS[dci].name,
            total: Math.round(sum(inWindow) / Math.max(1, reported)),
            ...cols.reduce((o: any, wk, idx) => { o[`d${idx}`] = weeks[wk]; return o; }, {}),
          };
        })
        .sort((a, b) => b.total - a.total);

      return {
        wholesaler: w.name,
        total: sum(children.map((c) => c.total)),
        ...cols.reduce((o: any, _wk, idx) => {
          o[`d${idx}`] = sum(children.map((c: any) => c[`d${idx}`]));
          return o;
        }, {}),
        callRecords: children,
      };
    })
    .sort((a, b) => b.total - a.total);

  return {
    kpis: [
      kpi('DOH (AVG)', avgDoh.toFixed(1), dohSeries, EMD_COLORS.primary, false),
      kpi('QTY On Hand', num(endSeries[endSeries.length - 1] ?? 0), endSeries, EMD_COLORS.primary),
      kpi('QTY Sold', num(totalSold), soldSeries, EMD_COLORS.returns),
      kpi('QTY Received', num(totalRcvd), rcvdSeries, EMD_COLORS.primary),
      kpi('Sell Through Rate', `${sellThrough.toFixed(1)}%`, soldSeries, EMD_COLORS.returns),
    ],

    labels: periods.map(shortDate),

    balanceBar: periods.map((period, i) => ({ period, doh: dohSeries[i] })),

    balanceLine: [
      ...periods.map((period, i) => ({ period, metric: 'QTY ON HAND', qty: endSeries[i] })),
      ...periods.map((period, i) => ({ period, metric: 'QTY SOLD', qty: soldSeries[i] })),
    ],

    dohBars: WHOLESALERS
      .map((w, i) => ({ name: w.name, value: daysOnHand(wsTotals[i].end, wsTotals[i].sold) }))
      .filter((d) => d.value > 0)
      .sort((a, b) => b.value - a.value),

    dateColumns: cols.map((i) => longDate(WEEKS[i])),
    rows,
  };
}

// ─── Unfiltered defaults ──────────────────────────────────────────────────────
// Components read these for field initialisers that run before any filter
// exists (colour maps, grid column definitions), then swap in derive* results.

export const SUMMARY_DEFAULT = deriveSummary();
export const DETAILS_DEFAULT = deriveDetails();
export const INVENTORY_DEFAULT = deriveInventory();
